import { inject } from "@angular/core";
import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { Router } from "@angular/router";
import { catchError, throwError } from "rxjs";
import { AuthService } from "./auth.service";
import { LimitService } from "./limit.service";

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const limits = inject(LimitService);
  req = req.clone({ withCredentials: true });
  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // The platform console is a SEPARATE principal with its own auth handling
      // (PlatformAuthService + platformGuard). Its probes (e.g. GET
      // /api/platform/me) legitimately 401 when no owner session exists; never
      // let the office interceptor react — otherwise the guard's own 401 probe
      // would clear office state and bounce the whole app to the office /login.
      if (req.url.includes("/api/platform")) {
        return throwError(() => err);
      }
      if (err.status === 401 && !req.url.includes("/auth/")) {
        auth.clear();
        router.navigate(["/login"]);
      }
      // 402 = subscription_required: the workspace's trial/subscription lapsed.
      // Don't log the user out — refresh their status so the paywall overlay
      // (auth.locked) appears, but never on the billing calls themselves.
      if (err.status === 402 && !req.url.includes("/billing")) {
        auth.refresh().subscribe({ next: () => {}, error: () => {} });
      }
      // 403 account_suspended: the platform owner banned this user or their
      // whole company mid-session. Flip the suspension flag so the shell swaps
      // to the "account suspended" screen instead of leaving the UI half-broken.
      if (
        err.status === 403 &&
        (err.error?.detail === "account_suspended" ||
          err.error === "account_suspended")
      ) {
        auth.markSuspended();
      }
      // 403 limit_reached / upgrade_required: structured entitlement errors.
      // Open the in-context purchase dialog wherever the user hit the wall.
      const detail = err.error?.detail;
      if (
        err.status === 403 &&
        detail &&
        typeof detail === "object" &&
        (detail.code === "limit_reached" || detail.code === "upgrade_required")
      ) {
        limits.open(detail);
      }
      return throwError(() => err);
    }),
  );
};
